/**
 * BankReport —— 銀行對帳報告（P2-5；`decisions/008` §4）
 * 由 `Bank.tsx` 抽出（頁面檔行數預算 ≤200）：
 * 月結單期末 vs 帳簿餘額 vs 差額 ＋ **兩邊未配對清單**（＝要解釋嘅嘢）＋ 忽略清單（唔准靜默）。
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fmtMoney, type BankReport as Report } from '../lib/api';

export default function BankReport({ report }: { report: Report | null }) {
  const { t } = useTranslation();
  if (!report) return <p className="note">{t('bank.empty')}</p>;
  return (
    <>
      <table className="ledger">
        <tbody>
          <tr><th>{t('bank.statement_closing')}</th><td className="num">{fmtMoney(report.statementClosingCents)}</td></tr>
          <tr><th>{t('bank.book_balance')}</th><td className="num">{fmtMoney(report.bookBalanceCents)}</td></tr>
          <tr>
            <th>{t('bank.difference')}</th>
            <td className={`num ${report.differenceCents === 0 ? 'pos' : 'neg'}`}>
              <b>{fmtMoney(report.differenceCents)}</b>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="note">
        {t('bank.statement_source', { src: report.statementClosingSource })}｜{t('bank.explain')}
      </p>

      <h3>
        {t('bank.unmatched_statement', { n: report.unmatched.statement.count })}　
        <small>（{t('bank.net')} {fmtMoney(report.unmatched.statement.netCents)}）</small>
      </h3>
      {report.unmatched.statement.rows.length === 0 ? <p className="note">{t('bank.empty')}</p> : (
        <ul className="list">
          {report.unmatched.statement.rows.map((r) => (
            <li key={r.id}>
              <span className="tag">{r.txn_date}</span><span>{r.description ?? '—'}</span>
              <b className={r.amount_cents < 0 ? 'neg' : 'pos'}>{fmtMoney(r.amount_cents)}</b>
            </li>
          ))}
        </ul>
      )}

      <h3>
        {t('bank.unmatched_book', { n: report.unmatched.book.count })}　
        <small>（{t('bank.net')} {fmtMoney(report.unmatched.book.netCents)}）</small>
      </h3>
      {report.unmatched.book.rows.length === 0 ? <p className="note">{t('bank.empty')}</p> : (
        <ul className="list">
          {report.unmatched.book.rows.map((r) => (
            <li key={r.voucher_id}>
              <span className="tag">{r.txn_date}</span>
              <Link to={`/vouchers/${r.voucher_id}`}>{r.voucher_no}</Link>
              <span>{r.purpose}</span>
              <b className={r.amount_cents < 0 ? 'neg' : 'pos'}>{fmtMoney(r.amount_cents)}</b>
            </li>
          ))}
        </ul>
      )}

      {report.ignored.count > 0 && (
        <>
          <h3>{t('bank.status_ignored')}（{report.ignored.count}）</h3>
          <ul className="list">
            {report.ignored.rows.map((r) => (
              <li key={r.id}>
                <span className="tag">{r.txn_date}</span><span>{r.description ?? '—'}</span>
                <b className={r.amount_cents < 0 ? 'neg' : 'pos'}>{fmtMoney(r.amount_cents)}</b>
                <span className="note">{r.ignored_reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
